import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { EvaluationContext, FieldDef, FormDefinition, FormValues } from '../form-engine';

export type FormType = 'PROPOSAL' | 'PLANNING' | 'EXECUTION' | 'EVALUATION';
export const FORM_TYPES: FormType[] = ['PROPOSAL', 'PLANNING', 'EXECUTION', 'EVALUATION'];
export type Phase = 'PAST' | 'CURRENT' | 'FUTURE';

export function isFormType(v: unknown): v is FormType {
  return typeof v === 'string' && (FORM_TYPES as string[]).includes(v);
}

export interface FormSummary {
  formType: FormType;
  phase: Phase;
  exists: boolean;
  editable: boolean;
  complete: boolean;
  errorCount: number;
  templateVersionNo: number;
  customFieldCount: number;
  lastModifiedAt: string | null;
  lastModifiedBy: string | null;
}

export interface InspectionForm {
  formType: FormType;
  phase: Phase;
  exists: boolean;
  editable: boolean;
  version: number | null;
  templateVersionId: string;
  templateVersionNo: number;
  definition: FormDefinition;
  templateDefinition: FormDefinition;
  customFields: FieldDef[];
  values: FormValues;
  state: Record<string, string>;
  errors: { field: string; code: string }[];
  complete: boolean;
  context: { today: string; timeframeFrom: string; timeframeTo: string; teamMembers: { id: string; name: string }[] };
  lastModifiedAt: string | null;
  lastModifiedBy: string | null;
}

/** The server context in the engine's shape. */
export function engineContext(form: Pick<InspectionForm, 'context'>): EvaluationContext {
  return {
    today: form.context.today,
    timeframe: form.context.timeframeFrom && form.context.timeframeTo ? { from: form.context.timeframeFrom, to: form.context.timeframeTo } : null,
    teamMembers: form.context.teamMembers,
  };
}

export function useInspectionForms(inspectionId: string | undefined) {
  return useQuery({
    queryKey: ['inspections', inspectionId, 'forms'],
    queryFn: () => api<FormSummary[]>(`/inspections/${inspectionId}/forms`),
    enabled: Boolean(inspectionId),
  });
}

export function useInspectionForm(inspectionId: string | undefined, type: FormType | undefined) {
  return useQuery({
    queryKey: ['inspections', inspectionId, 'forms', type],
    queryFn: () => api<InspectionForm>(`/inspections/${inspectionId}/forms/${type}`),
    enabled: Boolean(inspectionId && type),
  });
}

function useFormMutation<T>(inspectionId: string, type: FormType, path: 'values' | 'custom-fields', bodyKey: 'values' | 'customFields') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { data: T; version: number }) =>
      api<InspectionForm>(`/inspections/${inspectionId}/forms/${type}/${path}`, {
        method: 'PUT',
        body: { [bodyKey]: input.data, version: input.version },
        version: input.version,
      }),
    onSuccess: (form) => {
      qc.setQueryData(['inspections', inspectionId, 'forms', type], form);
      void qc.invalidateQueries({ queryKey: ['inspections', inspectionId] });
    },
  });
}

export function useSaveFormValues(inspectionId: string, type: FormType) {
  return useFormMutation<FormValues>(inspectionId, type, 'values', 'values');
}

export function useSaveCustomFields(inspectionId: string, type: FormType) {
  return useFormMutation<FieldDef[]>(inspectionId, type, 'custom-fields', 'customFields');
}
